import {
	AlertBar,
	Checkbox,
	FormattedText,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	RichTextEditor,
	RichTextEditorIntermediateStateHandlers,
	TextArea,
	TextInput,
	Toolbar,
	useRichTextEditorIntermediateState,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { AppType } from "client/Api/Apps/common";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import styles from "./EditEmbedApp.module.less";
import {
	SaveEmbedAppModel,
	EditEmbedAppViewModel,
	saveEmbedApp,
	EmbedAppContentKind
} from "client/Api/Apps/EditApp/saveApp";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { IValueInputAttachable } from "client/Forms/attachable";
import { isNilOrWhiteSpace } from "client/Misc/utils";
import { useEmbedAppPreviewDialog } from "./EmbedAppPreviewDialog";

const validation = createValidationConfiguration<SaveEmbedAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("html")
	.add(
		isNotNullNorUndefined()
			.validateOn("formSubmit"),
		isNotEmpty()
			.validateOn("formSubmit"),
		doesNotExceedLength(8_192)
			.validateOn("formSubmit")
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());

export type EditEmbedAppProps = {
	initialSettings: EditAppSettings<EditEmbedAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditEmbedApp = forwardRef(
	(
		props: EditEmbedAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [isAlertDismissed, setIsAlertDismissed] = useState<boolean>(false);
		const [formData, setFormData] = useState<SaveEmbedAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const [htmlStorage, setHtmlStorage] = useState<ReadonlyMap<EmbedAppContentKind, string | null>>(
			() => new Map()
		);

		const [previewDialog, openPreviewDialog] = useEmbedAppPreviewDialog();

		const productName: string = useMemo(
			() => userConfiguration().branding
				.productName,
			[]
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const { canBeConfigured } = app;

		const formHandler: IFormHandler<SaveEmbedAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const changeContentKindHandler = useCallback(
			(newKind: EmbedAppContentKind): void => {

				const { html, contentKind: oldKind } = formHandler.value;

				const previousHtml: string | nil = htmlStorage.get(newKind);

				setHtmlStorage(
					(x) => {
						const result = new Map<EmbedAppContentKind, string | null>(x);
						result.set(oldKind, html);
						return result;
					}
				);

				const htmlAttachable: IValueInputAttachable<string | null> = formHandler.for("html");

				htmlAttachable.reducer
					.setValue(previousHtml ?? null);

				htmlAttachable.validation
					.triggerInputFinished();
			},
			[formHandler, htmlStorage]
		);

		const prepareModelForSaving = useCallback(
			(model: SaveEmbedAppModel): SaveEmbedAppModel => {
				return {
					...model,
					html: canBeConfigured
						? model.html
						: null,
					navigationUrl: canBeConfigured
						? model.navigationUrl
						: null,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[canBeConfigured]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveEmbedAppModel>(
			formHandler,
			saveEmbedApp,
			AppType.Embed,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save: async () => {
					const result: EditAppSavedResult = await save();
					setHtmlStorage(new Map<EmbedAppContentKind, string | null>());
					return result;
				}
			}),
			[save]
		);

		const previewHandler = (): void => {
			const { contentKind, html } = formData;

			openPreviewDialog({
				contentKind,
				html: html ?? ""
			});
		};

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under(
				"contentKind",
				"html",
				"hideHeader",
				"hideFooter",
				"navigationUrl"
			)
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					{canBeConfigured && (
						<>
							<FormItem
								label={localize("_Content")}
								isRequired
							>
								<RadioButtonGroup
									{...formHandler.for("contentKind")
										.attachToRadioButtonGroup()
										.withOnChange(changeContentKindHandler)
									}
									options={contentKindOptions}
								/>

								<div className={styles.htmlEditor}>
									{formHandler.when(
										(x) => x.contentKind === EmbedAppContentKind.EmbedCode,
										(attachable) => (
											<TextArea
												{...attachable.for("html")
													.attachToTextArea()}
												isResizable
												rows={5}
											/>
										)
									)}

									{formHandler.when(
										(x) => x.contentKind === EmbedAppContentKind.CustomHtml,
										(attachable) => (
											<TextEditor
												attachable={attachable.for("html")}
											/>
										)
									)}
								</div>

								<ValidationError
									{...formHandler
										.for("html")
										.attachToValidationError()}
								/>

								<Toolbar>
									<Toolbar.Button
										trackingId="preview"
										kind="secondary"
										text={localize("_Preview")}
										onClick={previewHandler}
										isDisabled={isNilOrWhiteSpace(formData.html)}
									/>
								</Toolbar>

								{!isAlertDismissed && formHandler.when(
									(x) => x.contentKind === EmbedAppContentKind.EmbedCode,
									() => (
										<div className={styles.alertContainer}>
											<AlertBar
												kind="warning"
												isInline
												onDismiss={() => setIsAlertDismissed(true)}
											>
												<FormattedText
													text={localize("_EmbedAppWarningMessage")}
													thingsToReplace={[
														{
															what: "{ProductName}",
															filler: productName
														}
													]}
												/>
											</AlertBar>
										</div>
									)
								)}
							</FormItem>
							<FormItem>
								<Checkbox
									{...formHandler
										.for("hideHeader")
										.attachToCheckbox()}
									text={localize("_HideLiveTileHeader")}
								/>
								<ValidationError
									{...formHandler
										.for("hideHeader")
										.attachToValidationError()}
								/>
							</FormItem>
							<FormItem>
								<Checkbox
									{...formHandler
										.for("hideFooter")
										.attachToCheckbox()}
									text={localize("_HideLiveTileFooter")}
								/>
								<ValidationError
									{...formHandler
										.for("hideFooter")
										.attachToValidationError()}
								/>
							</FormItem>
							<FormItem label={localize("_LiveTileNavigationUrl")}>
								<TextInput
									{...formHandler.for("navigationUrl")
										.attachToTextInput()}
									placeholder={localize("_EnterNavigationUrl")}
								/>
								<ValidationError
									{...formHandler
										.for("navigationUrl")
										.attachToValidationError()}
								/>
							</FormItem>
						</>
					)}
				</EditAppBase>

				{previewDialog}
			</>
		);
	}
);

EditEmbedApp.displayName = "EditEmbedApp";

const contentKindOptions: readonly RadioButtonGroupItem<EmbedAppContentKind>[] = [
	{
		text: localize("_EmbedCode"),
		value: EmbedAppContentKind.EmbedCode
	},
	{
		text: localize("_TextEditor"),
		value: EmbedAppContentKind.CustomHtml
	}
];

type TextEditorProps = {
	attachable: IValueInputAttachable<string | null>;
};

const TextEditor = (props: TextEditorProps): ReactElement => {
	const {
		attachable
	} = props;

	const richTextEditorIntermediateState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();

	return (
		<RichTextEditor
			{...attachable.attachToRichTextEditor(richTextEditorIntermediateState)}
			height="resizable"
		/>
	);
};
TextEditor.displayName = "TextEditor";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditEmbedAppViewModel>
): SaveEmbedAppModel {
	const {
		html,
		navigationUrl,
		contentKind,
		hideFooter,
		hideHeader,
		maintenanceWindow,
		conditionalAccessSettings
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		html,
		navigationUrl,
		contentKind,
		hideFooter,
		hideHeader,
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings)
	};
}

